import { Component, OnInit, AfterViewInit, ViewChild, ChangeDetectorRef, ElementRef, HostListener } from '@angular/core';
import { KuiModalRefComponent } from 'app/key-ui/modal/modal-ref.component';
import { IKuiModalAction } from 'app/key-ui';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../modal';
import { FormBuilderDefinition, KeyFormBuilderComponent } from '../../form-builder';
import { MapControls, MapCoordinates } from '../../map/map.component';
import { LeafletMapComponent } from '../../map/leaflet.component';
import { debounce } from 'lodash';
import { MapToolsService } from '../../map/map-tools.service';
import { buildLocationIcon } from '../../map/map.utils';
import { SearchResult } from '../../search-results-list/search-results-list.component';
import { DecimalDegreesToDegreesPipe } from 'app/shared/pipes';
import { MapZonesService } from '../../map/map-zones.service';

export interface LocationSelectValuesActionsText {
    apply?: string;
    cancel?: string;
    clear?: string;
}

export interface LocationSelectValues {
    address: string;
    lat: number;
    lon: number;
    actionsText?: LocationSelectValuesActionsText;
    title?: string;
}

@Component({
    templateUrl: 'location-select-modal.component.html',
    styleUrls: ['location-select-modal.component.scss'],
    providers: [
        MapZonesService
    ],
    standalone: false
})
export class LocationSelectModalComponent extends KuiModalRefComponent<LocationSelectValues> implements OnInit, AfterViewInit {

    constructor(
        private i18n: TranslateService,
        private mapTools: MapToolsService,
        private ref: ChangeDetectorRef,
        private dec2deg: DecimalDegreesToDegreesPipe
    ) {
        super();
    }
    mapControls = [
        MapControls.zoom,
        MapControls.fullscreen,
    ];

    searchValue: string;
    searchResults: SearchResult<MapCoordinates>[];
    showSearchResults: boolean;
    debouncedUpdateSearchValue = debounce(this.updateSearchValue, 500, { trailing: true, leading: false });

    values: LocationSelectValues = { lat: null, lon: null, address: null };
    form: FormBuilderDefinition = {
        groups: [{
            fields: [
                { type: 'number', id: 'lat', title: 'LAT', required: true, onChange: async (_field, values) => this.debouncedGetAddressAndUpdateFormValues(values) },
                { type: 'number', id: 'lon', title: 'LON', required: true, onChange: async (_field, values) => this.debouncedGetAddressAndUpdateFormValues(values) },
            ],
        }],
    };
    modalActions: IKuiModalAction[];
    debouncedGetAddressAndUpdateFormValues = debounce(this.getAddressAndUpdateValues, 300, { trailing: true, leading: false });
    debouncedGetupdateFormValues = debounce(this.updateValues, 300, { trailing: true, leading: false });

    loading = false;

    @ViewChild(LeafletMapComponent, { static: true }) map: LeafletMapComponent;
    @ViewChild('searchInput') searchInput: ElementRef;
    @ViewChild(KeyFormBuilderComponent, { static: true }) formRef: KeyFormBuilderComponent;

    static open(modal: ModalService, options: LocationSelectValues): Promise<LocationSelectValues> {
        return new Promise<LocationSelectValues>((resolve, reject) => {
            try {
                modal.open<LocationSelectValues>(LocationSelectModalComponent, {
                    data: options,
                    actions: {
                        close: () => {
                            modal.close();
                            resolve(null);
                        },
                        apply: (result: LocationSelectValues) => {
                            modal.close();
                            resolve(result);
                        },
                    },

                });
            } catch (err) {
                reject(err);
            }
        });

    }

    @HostListener('document:click', ['$event']) closeOnOutsideClick(e) {
        if (!e.target.contains(this.searchInput.nativeElement)) {
            this.showSearchResults = false;
        }
    }

    ngOnInit() {
        this.updateModalActions(false, this.data.actionsText);
        this.on(this.mapTools.loading$, loading => this.loading = !!loading);
    }

    ngAfterViewInit() {
        setTimeout(() => {
            if (this.data) {
                this.updateValues(this.data);
            }
        });
    }

    updateModalActions(clear: boolean, text: LocationSelectValuesActionsText) {
        text = {
            cancel: this.i18n.instant('DIALOG.CANCEL'),
            clear: this.i18n.instant('DIALOG.CLEAR'),
            apply: this.i18n.instant('DIALOG.SAVE'),
            ...text,
        };

        this.modalActions = [{
            text: text.cancel,
            style: 'secondary',
            keypress: 27,
            action: () => {
                this.actions.close(false);
            },
        }, clear && {
            text: text.clear,
            style: 'secondary',
            keypress: 27,
            action: () => {
                this.updateValues(null, null, true);
                return true;
            },
        }, {
            text: text.apply,
            style: 'primary',
            action: () => {
                if (!this.formRef.validate()) {
                    return true;
                }
                this.actions.apply(this.values);
            },
        }].filter(x => x);
    }

    async getAddressAndUpdateValues(values: MapCoordinates) {
        if (values) {
            this.clearSearch();
            const { address } = await this.mapTools.getCoordinatesDetails(values.lat, values.lon);
            this.updateValues({
                ...values,
                address,
            });
        }
    }

    updateValues(details: MapCoordinates, id?: string, clear?: boolean) {
        this.hideSearchList();
        const { lat, lon, address } = details || {} as MapCoordinates;

        // keep previous lat/lon values if nothing new comes through unless clear was set
        this.values = !clear ? {
            lat: lat !== undefined ? lat : this.values.lat,
            lon: lon !== undefined ? lon : this.values.lon,
            address: address || `${this.dec2deg.transform(lat || this.values.lat)}, ${this.dec2deg.transform(lon || this.values.lon)}`,
        } : { lat: null, lon: null, address: null };

        if (lat !== undefined && lon !== undefined) {
            // remove the default markers of coordinate selection and search and replace them with a our own marker so it always looks the same
            if (!id || id !== 'location-select-marker') {
                this.addMarker(lat, lon, address);
            }
            this.updateModalActions(true, this.data.actionsText);
        } else {
            this.updateModalActions(false, this.data.actionsText);
            this.clearSearch();
            this.map.clearMarkers();
            this.map.getCoordinates();
        }
    }

    updateSearchValue(value: string) {
        if (!value) {
            this.clearSearch();
            return;
        }

        this.searchValue = value;
        this.showSearchResults = true;
        this.doSearch(value);
        this.ref.markForCheck();
    }

    clearSearch() {
        this.searchValue = '';
        this.doSearch(null);
        this.ref.markForCheck();
    }

    async doSearch(term: string) {
        this.showSearchResults = !!term;

        const action = marker => {
            this.addMarker(marker.lat, marker.lon, marker.address);
        };

        this.searchResults = await this.mapTools.doSearch(term, action);
        this.ref.markForCheck();
    }

    blurSearchInput() {
        if (this.searchInput) {
            this.searchInput.nativeElement.blur();
        }
    }

    hideSearchList() {
        this.showSearchResults = false;
        this.blurSearchInput();
    }

    addMarker(lat: number, lon: number, address: string) {
        if (lat && lon) {
            this.map.addMapMarker({
                name: address,
                id: 'location-select-marker',
                lat,
                lon,
                draggable: true,
                dragged: (latitude, longitude) => {
                    this.getAddressAndUpdateValues({ lat: latitude, lon: longitude });
                },
                getIcon: () => buildLocationIcon('map-marker-alt'),
            });
            this.map.removeCoordinates();
            this.map.setZoom(13); // the default zoom is too much
        }
    }

    detachModal() {
        this.actions.close(false);
    }

}
